import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "../../utils/common/cn";

export function LinkedPanel({
  to,
  title,
  description,
  statLabel,
  stat,
  detail,
  trend,
  goodDirection = "up",
  chart,
  footnote,
  className,
}: {
  to: string;
  title: string;
  description?: string;
  statLabel: string;
  stat: string;
  /** A second figure for the same card, in plain grey under the stat (before the change line): e.g. the dollar exposure beside a count. */
  detail?: string;
  trend?: { value: string; direction: "up" | "down" | "flat" };
  /** Which way is good news for this figure: green for it, red for the other. Defaults to "up". */
  goodDirection?: "up" | "down";
  /** A real embedded chart — sparkline, mini bar comparison, whatever fits. Never omit on a drill-down card. */
  chart: ReactNode;
  footnote?: ReactNode;
  className?: string;
}) {
  return (
    <Link
      to={to}
      className={cn(
        "flex flex-col border border-border bg-card p-5 text-left transition-colors hover:border-primary/60 hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
        className,
      )}
    >
      <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground">{title}</h2>
      {description ? <p className="mt-1 text-xs text-muted-foreground">{description}</p> : null}

      <div className="mt-4 flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {statLabel}
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">{stat}</p>
          {detail ? <p className="mt-1 text-xs font-medium tabular-nums text-muted-foreground">{detail}</p> : null}
          {trend ? (
            <p
              className={cn(
                "mt-1 text-xs font-medium tabular-nums",
                trend.direction === "flat"
                  ? "text-muted-foreground"
                  : trend.direction === goodDirection
                    ? "text-approve"
                    : "text-decline",
              )}
            >
              {trend.direction === "up" ? "▲" : trend.direction === "down" ? "▼" : "■"}{" "}
              {trend.value}
            </p>
          ) : null}
        </div>
        <div className="h-16 w-32 shrink-0">{chart}</div>
      </div>

      {footnote ? (
        <div className="mt-3 text-xs leading-relaxed text-muted-foreground">{footnote}</div>
      ) : null}
    </Link>
  );
}
